import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { TITLE_BLOCK, DRAWING_COLORS, LINE_STYLES } from './constants';

const JP_FONT = "'Noto Sans JP', sans-serif";
const C = DRAWING_COLORS;

type ApprovalEntry = { role: string; name: string; date?: string };

type TitleBlockProps = {
  x: number;
  y: number;
  productName?: string;
  seriesName?: string;
  scale?: string;
  drawingNumber?: string;
  companyName?: string;
  approvalChain?: ApprovalEntry[];
  notes?: string;
  sheetInfo?: string;
  /** Projection convention label, e.g. "第三角法" or "Third-Angle Projection". */
  projectionMethod?: string;
};

export function TitleBlock({
  x,
  y,
  productName,
  seriesName,
  scale = '1:1',
  drawingNumber,
  companyName = 'AgentCAD',
  approvalChain,
  notes,
  sheetInfo = '1 / 1',
  projectionMethod,
}: TitleBlockProps) {
  const { t } = useTranslation('common');
  const clipId = useId().replace(/:/g, '_');
  const { width: w, height: h, fontSize } = TITLE_BLOCK;
  const border = LINE_STYLES.border.strokeWidth;

  const resolvedProductName = productName ?? t('drawing.untitled');

  // Default approval chain
  const chain = approvalChain ?? [
    { role: t('drawing.draftedBy'), name: '' },
    { role: t('drawing.checkedBy'), name: '' },
    { role: t('drawing.approvedBy'), name: '' },
  ];

  const displayDate = new Date().toISOString().slice(0, 10);
  const displayDrawingNumber = drawingNumber ?? `図面-${displayDate.replace(/-/g, '')}`;

  // Layout constants
  const topSectionH = 22; // approval row section
  const middleSectionH = 20; // product name + drawing number
  const bottomSectionH = h - topSectionH - middleSectionH; // company name

  // Top section: approval columns
  const notesColW = w * 0.25;
  const scaleColW = w * 0.15;
  const chainColW = (w - notesColW - scaleColW) / chain.length;
  const topRowH = 8; // header row
  const topDataH = topSectionH - topRowH;

  // Middle section
  const nameColW = w * 0.5;
  const numColW = w - nameColW;
  const nameRowH = middleSectionH * 0.6;
  const seriesRowH = middleSectionH - nameRowH;

  // Shrink title font for long names — aggressive scaling for very long product descriptions
  const titleLen = resolvedProductName.length;
  const titleFontSize = titleLen > 60 ? fontSize.title * 0.3
    : titleLen > 40 ? fontSize.title * 0.4
    : titleLen > 30 ? fontSize.title * 0.5
    : titleLen > 20 ? fontSize.title * 0.65
    : titleLen > 14 ? fontSize.title * 0.8
    : fontSize.title;

  // Truncate if still too long for the cell
  const maxTitleChars = Math.floor(nameColW / (titleFontSize * 0.6));
  const displayProductName = resolvedProductName.length > maxTitleChars
    ? resolvedProductName.substring(0, maxTitleChars - 1) + '…'
    : resolvedProductName;

  console.log(`[TitleBlock] productName="${resolvedProductName.substring(0, 40)}..." len=${titleLen}, fontSize=${titleFontSize.toFixed(1)}, maxChars=${maxTitleChars}`);

  return (
    <g className="title-block" transform={`translate(${x}, ${y})`}>
      <defs>
        <clipPath id={`${clipId}-tb`}>
          <rect x={0} y={0} width={w} height={h} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId}-tb)`}>
        {/* Outer border */}
        <rect x={0} y={0} width={w} height={h}
          fill="none" stroke={C.line} strokeWidth={border} />

        {/* ═══ TOP SECTION: 年月日 | 記事 | 縮尺 | approval chain ═══ */}
        {/* Horizontal divider below top section */}
        <line x1={0} y1={topSectionH} x2={w} y2={topSectionH}
          stroke={C.line} strokeWidth={border * 0.7} />
        {/* Header row divider */}
        <line x1={0} y1={topRowH} x2={w} y2={topRowH}
          stroke={C.line} strokeWidth={border * 0.3} />

        {/* 年月日 column */}
        <line x1={notesColW} y1={0} x2={notesColW} y2={topSectionH}
          stroke={C.line} strokeWidth={border * 0.3} />
        <text x={notesColW / 2} y={topRowH / 2}
          textAnchor="middle" dominantBaseline="central"
          fontSize={fontSize.label} fontFamily={JP_FONT} fill={C.text} fontWeight="500">
          {t('drawing.dateLabel')}
        </text>
        <text x={notesColW / 2} y={topRowH + topDataH / 2}
          textAnchor="middle" dominantBaseline="central"
          fontSize={fontSize.value} fontFamily={JP_FONT} fill={C.text}>
          {notes || displayDate}
        </text>

        {/* 縮尺 column */}
        <line x1={notesColW + scaleColW} y1={0} x2={notesColW + scaleColW} y2={topSectionH}
          stroke={C.line} strokeWidth={border * 0.3} />
        <text x={notesColW + scaleColW / 2} y={topRowH / 2}
          textAnchor="middle" dominantBaseline="central"
          fontSize={fontSize.label} fontFamily={JP_FONT} fill={C.text} fontWeight="500">
          {t('drawing.scaleLabel')}
        </text>
        <text x={notesColW + scaleColW / 2} y={topRowH + topDataH / 2}
          textAnchor="middle" dominantBaseline="central"
          fontSize={fontSize.value} fontFamily={JP_FONT} fill={C.text} fontWeight="600">
          {scale}
        </text>

        {/* Approval chain columns */}
        {chain.map((entry, i) => {
          const colX = notesColW + scaleColW + i * chainColW;
          return (
            <g key={entry.role}>
              {i > 0 && (
                <line x1={colX} y1={0} x2={colX} y2={topSectionH}
                  stroke={C.line} strokeWidth={border * 0.3} />
              )}
              {/* Also draw left border for first chain column */}
              {i === 0 && (
                <line x1={colX} y1={0} x2={colX} y2={topSectionH}
                  stroke={C.line} strokeWidth={border * 0.3} />
              )}
              <text x={colX + chainColW / 2} y={topRowH / 2}
                textAnchor="middle" dominantBaseline="central"
                fontSize={fontSize.label} fontFamily={JP_FONT} fill={C.text} fontWeight="500">
                {entry.role}
              </text>
              {entry.name && (
                <text x={colX + chainColW / 2} y={topRowH + topDataH * 0.4}
                  textAnchor="middle" dominantBaseline="central"
                  fontSize={fontSize.value} fontFamily={JP_FONT} fill={C.text}>
                  {entry.name}
                </text>
              )}
              {entry.date && (
                <text x={colX + chainColW / 2} y={topRowH + topDataH * 0.8}
                  textAnchor="middle" dominantBaseline="central"
                  fontSize={fontSize.label} fontFamily={JP_FONT} fill={C.text} opacity={0.7}>
                  {entry.date}
                </text>
              )}
            </g>
          );
        })}

        {/* ═══ MIDDLE SECTION: 品名 + 図面番号 ═══ */}
        <line x1={0} y1={topSectionH + middleSectionH} x2={w} y2={topSectionH + middleSectionH}
          stroke={C.line} strokeWidth={border * 0.7} />
        {/* Vertical divider between name and number */}
        <line x1={nameColW} y1={topSectionH} x2={nameColW} y2={topSectionH + middleSectionH}
          stroke={C.line} strokeWidth={border * 0.3} />

        {/* 品名 label */}
        <text x={2} y={topSectionH + 3}
          dominantBaseline="hanging"
          fontSize={fontSize.label} fontFamily={JP_FONT} fill={C.text} opacity={0.6}>
          {t('drawing.itemNameLabel')}
        </text>
        {/* Product name — clipped to cell */}
        <defs>
          <clipPath id={`${clipId}-name-clip`}>
            <rect x={0} y={topSectionH} width={nameColW} height={nameRowH} />
          </clipPath>
        </defs>
        <text x={nameColW / 2} y={topSectionH + nameRowH / 2 + 1}
          textAnchor="middle" dominantBaseline="central"
          fontSize={titleFontSize} fontFamily={JP_FONT} fill={C.text} fontWeight="700"
          clipPath={`url(#${clipId}-name-clip)`}>
          {displayProductName}
        </text>
        {/* Series name row */}
        {seriesName && (
          <>
            <line x1={0} y1={topSectionH + nameRowH} x2={nameColW} y2={topSectionH + nameRowH}
              stroke={C.line} strokeWidth={border * 0.2} />
            <text x={nameColW / 2} y={topSectionH + nameRowH + seriesRowH / 2}
              textAnchor="middle" dominantBaseline="central"
              fontSize={fontSize.value} fontFamily={JP_FONT} fill={C.text}>
              {seriesName}
            </text>
          </>
        )}

        {/* 図面番号 label */}
        <text x={nameColW + 2} y={topSectionH + 3}
          dominantBaseline="hanging"
          fontSize={fontSize.label} fontFamily={JP_FONT} fill={C.text} opacity={0.6}>
          {t('drawing.drawingNumberLabel')}
        </text>
        {/* Drawing number */}
        <text x={nameColW + numColW / 2} y={topSectionH + middleSectionH / 2 + 1}
          textAnchor="middle" dominantBaseline="central"
          fontSize={fontSize.value} fontFamily={JP_FONT} fill={C.text} fontWeight="600">
          {displayDrawingNumber}
        </text>

        {/* ═══ BOTTOM SECTION: Company name ═══ */}
        <text x={w / 2} y={topSectionH + middleSectionH + bottomSectionH / 2}
          textAnchor="middle" dominantBaseline="central"
          fontSize={companyName.length > 15 ? fontSize.company * 0.7 : fontSize.company}
          fontFamily={JP_FONT} fill={C.text}
          fontWeight="500" letterSpacing={companyName.length > 10 ? '1' : '3'}>
          {companyName}
        </text>

        {/* Sheet info in bottom-right corner */}
        <text x={w - 3} y={h - 2}
          textAnchor="end" dominantBaseline="auto"
          fontSize={fontSize.label} fontFamily={JP_FONT} fill={C.text} opacity={0.5}>
          {sheetInfo}
        </text>

        {/* Projection method label in bottom-left corner so the manufacturer
            knows which angle convention the views follow. */}
        {projectionMethod && (
          <text x={3} y={h - 2}
            textAnchor="start" dominantBaseline="auto"
            fontSize={fontSize.label} fontFamily={JP_FONT} fill={C.text} opacity={0.6}
            fontWeight="500">
            {projectionMethod}
          </text>
        )}
      </g>
    </g>
  );
}
